import React, { useState, FC, useRef, RefObject, useEffect } from 'react';
import {
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    AlertTitle,
    Box,
    Grid,
    TextField,
    InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { Paycode } from 'repository/timekeeper/@types/PaycodeModel';
import { DatePattern } from 'repository/timekeeper/@types/DatePattern';
import { GrantTypeEnum } from 'repository/timekeeper/@types/TypeGrant';
import { GrantConcessionEnum } from 'repository/timekeeper/@types/TypeGrantConcession';
import { TipoEquivalenciaEnum } from 'repository/timekeeper/@types/TypeEquivalentia';
import { TipoImpactoCambio } from 'repository/timekeeper/@types/TypeChangeImpact';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
import { ApplicationEnum } from 'repository/timekeeper/@types/TypeAplicationEnum';
import { CalificadorCreate } from 'repository/timekeeper/@types/Calificador';
import { TipoCalificadorEnum } from 'repository/timekeeper/@types/TypeCalificador';
import { TipoProrrateoEnum } from 'repository/timekeeper/@types/TypePorrateo';
import { Grant } from 'repository/timekeeper/@types/Grant';
import { TipoOrtorgarConcesion } from 'repository/timekeeper/@types/TypeGiveGrant';
import { GrantFormFijo, GrantFormFijoData } from './GrantFormFijo';
import { GrantFormPercibido, GrantFormPercibidoData } from './GrantFormPercibido';
import { GrantFijoCreateSchema } from 'schemas/Grants';
import { nameValidation, normalizeString } from 'utils/Validations';

interface UpdateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: Grant[];
    rowToEdit: Grant;
    reloading: () => void;
    cleanSelected: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable nodeRef={nodeRef as RefObject<HTMLDivElement>} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

export const UpdateGrant: FC<UpdateProps> = ({ open, setOpen, rows, rowToEdit, reloading, cleanSelected }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    // Estados básicos
    const [grantType, setGrantType] = useState<GrantTypeEnum>(GrantTypeEnum.FIJO);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    // Estados para tipo FIJO
    const [fijoFormData, setFijoFormData] = useState<GrantFormFijoData>({
        grantConcession: GrantConcessionEnum.REPETITIVO,
        datePatternId: '',
        expiration: false,
        expirationNumber: 1,
        expirationUnit: FrequencyUnitTime.DIAS,
        fullTimeEquivalents: TipoEquivalenciaEnum.NO_MODIFICA_CONCESION,
        paycodeEtcId: '',
        impactChangeType: TipoImpactoCambio.UTILIZAR_NORMA_ACUMULACION,
        impactRecalculate: false,
        qualifiers: [],
        apportionmentType: TipoProrrateoEnum.FECHA_CONTRATACION,
    });

    // Estados para tipo PERCIBIDO
    const [percibidoFormData, setPercibidoFormData] = useState<GrantFormPercibidoData>({
        basedOn: TipoOrtorgarConcesion.INMEDIATAMENTE,
        paycodeId: '',
        datePatternId: '',
        applicationMode: ApplicationEnum.USAR_CANTIDAD_CONCESION,
        applicationModeAmount: 0,
        applicationModeMove: false,
        applicationModeDatePatternId: '',
        expiration: false,
        expirationNumber: 1,
        expirationUnit: FrequencyUnitTime.DIAS,
        qualifiers: [],
    });

    // Datos para selects
    const [availablePaycodes, setAvailablePaycodes] = useState<Paycode[]>([]);
    const [availableDatePatterns, setAvailableDatePatterns] = useState<DatePattern[]>([]);

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [alerts, setAlerts] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        name?: string;
        description?: string;
        expiration_number?: string;
    }>({});

    useEffect(() => {
        const loadData = async () => {
            setLoadingData(true);
            try {
                const [paycodes, datePatterns] = await Promise.all([timekeeperService.paycodes.getPaycodes(), timekeeperService.date_pattern.getAll()]);
                setAvailablePaycodes(paycodes as unknown as Paycode[]);
                setAvailableDatePatterns(datePatterns);
            } catch (error) {
                console.error('Error loading data:', error);
                enqueueSnackbar(t('Error al cargar datos'), { variant: 'error' });
            } finally {
                setLoadingData(false);
            }
        };

        const preloadRow = () => {
            if (!rowToEdit) return;
            console.log('Preloading row:', rowToEdit);
            setName(rowToEdit.name || '');
            setDescription(rowToEdit.description || '');
            if (rowToEdit.grant_type !== undefined) setGrantType(rowToEdit.grant_type as GrantTypeEnum);

            const hasExpiration = (rowToEdit as any).expiration === true;

            // FIJO
            setFijoFormData({
                grantConcession: (rowToEdit as any).grant_concession ?? GrantConcessionEnum.REPETITIVO,
                datePatternId: ((rowToEdit as any).date_pattern_id ?? '') as number | '',
                expiration: hasExpiration,
                expirationNumber: hasExpiration ? ((rowToEdit as any).expiration_number ?? 1) : 1,
                expirationUnit: hasExpiration ? (((rowToEdit as any).expiration_unit_date ?? FrequencyUnitTime.DIAS) as FrequencyUnitTime) : FrequencyUnitTime.DIAS,
                fullTimeEquivalents: ((rowToEdit as any).full_time_equivalents ?? TipoEquivalenciaEnum.NO_MODIFICA_CONCESION) as TipoEquivalenciaEnum,
                paycodeEtcId: ((rowToEdit as any).paycode_id ?? '') as number | '',
                impactChangeType: ((rowToEdit as any).impact_change_type ?? TipoImpactoCambio.UTILIZAR_NORMA_ACUMULACION) as TipoImpactoCambio,
                impactRecalculate: Boolean((rowToEdit as any).impact_recalculate),
                qualifiers: ((rowToEdit as any).qualifiers ?? []) as CalificadorCreate[],
                apportionmentType: ((rowToEdit as any).apportionment_type ?? '') as TipoProrrateoEnum | '',
            });

            // PERCIBIDO
            setPercibidoFormData({
                basedOn: ((rowToEdit as any).type_give_grant ?? TipoOrtorgarConcesion.INMEDIATAMENTE) as TipoOrtorgarConcesion.INMEDIATAMENTE | TipoOrtorgarConcesion.PATRON_DE_FECHAS,
                paycodeId: ((rowToEdit as any).paycode_id ?? '') as number | '',
                datePatternId: ((rowToEdit as any).date_pattern_id ?? '') as number | '',
                applicationMode: ((rowToEdit as any).application_mode ?? ApplicationEnum.USAR_CANTIDAD_CONCESION) as ApplicationEnum,
                applicationModeAmount: ((rowToEdit as any).application_mode_amount ?? 0) as number,
                applicationModeMove: Boolean((rowToEdit as any).application_mode_move_reset),
                applicationModeDatePatternId: ((rowToEdit as any).application_mode_dates_pattern_id ?? '') as number | '',
                expiration: hasExpiration,
                expirationNumber: hasExpiration ? ((rowToEdit as any).expiration_number ?? 1) : 1,
                expirationUnit: hasExpiration ? (((rowToEdit as any).expiration_unit_date ?? FrequencyUnitTime.DIAS) as FrequencyUnitTime) : FrequencyUnitTime.DIAS,
                qualifiers: ((rowToEdit as any).qualifiers ?? []) as CalificadorCreate[],
            });
        };

        if (open) {
            loadData();
            preloadRow();
        }
    }, [open, rowToEdit, enqueueSnackbar, t]);

    const existName = (name: string) => {
        const normalizedInput = normalizeString(name);
        const selected = rows.find((row) => row.id === rowToEdit?.id);
        if (selected && normalizeString(selected.name) === normalizedInput) {
            return false;
        }

        return rows.some((row) => normalizeString(row.name) === normalizedInput);
    };

    const closeForm = () => {
        setGrantType(GrantTypeEnum.FIJO);
        setName('');
        setDescription('');
        setFijoFormData({
            grantConcession: GrantConcessionEnum.REPETITIVO,
            datePatternId: '',
            expiration: false,
            expirationNumber: 1,
            expirationUnit: FrequencyUnitTime.DIAS,
            fullTimeEquivalents: TipoEquivalenciaEnum.NO_MODIFICA_CONCESION,
            paycodeEtcId: '',
            impactChangeType: TipoImpactoCambio.UTILIZAR_NORMA_ACUMULACION,
            impactRecalculate: false,
            qualifiers: [],
            apportionmentType: '',
        });
        setPercibidoFormData({
            basedOn: TipoOrtorgarConcesion.INMEDIATAMENTE,
            paycodeId: '',
            datePatternId: '',
            applicationMode: ApplicationEnum.USAR_CANTIDAD_CONCESION,
            applicationModeAmount: 0,
            applicationModeMove: false,
            applicationModeDatePatternId: '',
            expiration: false,
            expirationNumber: 1,
            expirationUnit: FrequencyUnitTime.DIAS,
            qualifiers: [],
        });
        setOpen(false);
        setAlerts(false);
        setLoading(false);
        cleanSelected();
    };

    const changes = () => {
        if (name !== '') {
            setAlerts(true);
        } else {
            closeForm();
        }
    };

    const Update = async () => {
        setLoading(true);
        try {
            let dataToUpdate: any = {
                name,
                grant_id: (rowToEdit as any)?.grant_id,
                description: description || null,
                grant_type: grantType,
                qualifiers: (grantType === GrantTypeEnum.FIJO ? fijoFormData.qualifiers : percibidoFormData.qualifiers).filter((q) => q.qualifier_type !== TipoCalificadorEnum.NINGUNO),
            };

            if (grantType === GrantTypeEnum.FIJO) {
                dataToUpdate = {
                    ...dataToUpdate,
                    grant_concession: fijoFormData.grantConcession,
                    date_pattern_id: fijoFormData.datePatternId !== '' ? fijoFormData.datePatternId : null,
                    full_time_equivalents: fijoFormData.fullTimeEquivalents,
                    paycode_id: fijoFormData.fullTimeEquivalents === TipoEquivalenciaEnum.MODIFICA_CONCESION_PAYCODE && fijoFormData.paycodeEtcId !== '' ? fijoFormData.paycodeEtcId : null,
                    impact_change_type: fijoFormData.impactChangeType,
                    impact_recalculate: fijoFormData.impactRecalculate,
                    apportionment_type: fijoFormData.apportionmentType !== '' ? fijoFormData.apportionmentType : null,
                };
                dataToUpdate.expiration = fijoFormData.expiration;
                dataToUpdate.expiration_number = fijoFormData.expiration ? fijoFormData.expirationNumber : null;
                dataToUpdate.expiration_unit_date = fijoFormData.expiration ? fijoFormData.expirationUnit : null;
            } else if (grantType === GrantTypeEnum.PERCIBIDO) {
                dataToUpdate = {
                    ...dataToUpdate,
                    paycode_id: percibidoFormData.paycodeId,
                    type_give_grant: percibidoFormData.basedOn,
                    date_pattern_id: percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS && percibidoFormData.datePatternId !== '' ? percibidoFormData.datePatternId : null,
                    application_mode: percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS ? percibidoFormData.applicationMode : null,
                    application_mode_amount:
                        percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS && percibidoFormData.applicationMode === ApplicationEnum.ACOTAR_EN_INCREMENTO
                            ? percibidoFormData.applicationModeAmount
                            : null,
                    application_mode_move_reset:
                        percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS && percibidoFormData.applicationMode === ApplicationEnum.ACOTAR_EN_INCREMENTO
                            ? percibidoFormData.applicationModeMove
                            : null,
                    application_mode_pattern_date_id:
                        percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS &&
                        percibidoFormData.applicationMode === ApplicationEnum.ACOTAR_EN_INCREMENTO &&
                        percibidoFormData.applicationModeMove &&
                        percibidoFormData.applicationModeDatePatternId !== ''
                            ? percibidoFormData.applicationModeDatePatternId
                            : null,
                };
                dataToUpdate.expiration = percibidoFormData.expiration;
                dataToUpdate.expiration_number = percibidoFormData.expiration ? percibidoFormData.expirationNumber : null;
                dataToUpdate.expiration_unit_date = percibidoFormData.expiration ? percibidoFormData.expirationUnit : null;
            }

            const grantId = (rowToEdit as any)?.grant_id;
            if (grantId === undefined || grantId === null) {
                enqueueSnackbar(t('accruals.grants.dialog.alerts.no_id_found'), { variant: 'error' });
                setLoading(false);
                return;
            }
            console.log('Grant to update:', dataToUpdate);
            const updated = await timekeeperService.grants.update(grantId, dataToUpdate);
            console.log('Updated Grant:', updated);

            enqueueSnackbar(t('accruals.grants.dialog.alerts.update.success'), { variant: 'success' });
            closeForm();
            reloading();
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.grants.dialog.alerts.update.error'), { variant: 'error' });
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="xl"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.grants.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant="filled"
                        severity="warning"
                        sx={{ mt: 2, mx: 3 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                {loadingData ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                fullWidth
                                label={t('accruals.grants.dialog.fields.name')}
                                value={name}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setName(value);
                                    // Valida formato permitido y reglas del schema
                                    const formatIsValid = nameValidation(value);
                                    const result = GrantFijoCreateSchema.shape.name.safeParse(value);
                                    setValidationErrors((prev) => ({
                                        ...prev,
                                        name: !formatIsValid ? t('accruals.grants.dialog.alerts.invalid_name') : result.success ? undefined : result.error.issues[0]?.message,
                                    }));
                                }}
                                required
                                autoComplete="off"
                                slotProps={{
                                    htmlInput: { maxLength: 100, minLength: 3 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${name.length}/100`}</InputAdornment>,
                                    },
                                }}
                                helperText={(existName(name) && name !== '' ? t('accruals.grants.dialog.alerts.name_exist') : '') || validationErrors.name}
                                error={(existName(name) && name !== '') || !!validationErrors.name}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                fullWidth
                                label={t('accruals.grants.dialog.fields.description')}
                                value={description}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDescription(value);
                                    // Validar descripción
                                    if (value) {
                                        const result = GrantFijoCreateSchema.shape.description.safeParse(value);
                                        setValidationErrors((prev) => ({
                                            ...prev,
                                            description: result.success ? undefined : result.error.issues[0]?.message,
                                        }));
                                    } else {
                                        setValidationErrors((prev) => ({
                                            ...prev,
                                            description: undefined,
                                        }));
                                    }
                                }}
                                autoComplete="off"
                                error={!!validationErrors.description}
                                helperText={validationErrors.description}
                                slotProps={{
                                    htmlInput: { maxLength: 255 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${description.length}/255`}</InputAdornment>,
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <FormControl fullWidth disabled>
                                <InputLabel>{t('accruals.grants.dialog.fields.grant_type')}</InputLabel>
                                <Select value={grantType} onChange={(e) => setGrantType(e.target.value as GrantTypeEnum)} label={t('accruals.grants.dialog.fields.grant_type')}>
                                    <MenuItem value={GrantTypeEnum.FIJO}>{t('accruals.grants.dialog.fields.fixed')}</MenuItem>
                                    <MenuItem value={GrantTypeEnum.PERCIBIDO}>{t('accruals.grants.dialog.fields.perceived')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            {grantType === GrantTypeEnum.FIJO ? (
                                <GrantFormFijo formData={fijoFormData} setFormData={setFijoFormData} datePatterns={availableDatePatterns} paycodes={availablePaycodes} />
                            ) : (
                                <GrantFormPercibido formData={percibidoFormData} setFormData={setPercibidoFormData} paycodes={availablePaycodes} datePatterns={availableDatePatterns} />
                            )}
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => changes()} variant="outlined" disabled={loading}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    onClick={Update}
                    variant="contained"
                    loading={loading}
                    disabled={
                        loading ||
                        !name ||
                        !nameValidation(name) ||
                        (grantType === GrantTypeEnum.PERCIBIDO && percibidoFormData.paycodeId === '') ||
                        existName(name) ||
                        !!validationErrors.name ||
                        !!validationErrors.description
                    }
                >
                    {t('general.buttons.edit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
